import { Loader2, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { DeviceAgent } from "./devices-api";
import { deviceLabel, isDeviceReachable } from "./devices-helpers";
import { useDeleteDeviceMutation } from "./use-devices-query";

/**
 * Potwierdzenie trwałego usunięcia urządzenia z książki adresowej. Operacja
 * nieodwracalna (kasuje wpis + historię sesji zdalnych do tego urządzenia), więc
 * wymaga osobnego kliknięcia — w odróżnieniu od „odwołania", które tylko oznacza
 * urządzenie jako wycofane.
 *
 * Gdy urządzenie jest online, dochodzi opcja „odinstaluj agenta z komputera" —
 * backend zleca wtedy agentowi samodeinstalację (NSIS uninstaller) PRZED
 * usunięciem wpisu. Offline → opcji nie ma (komenda i tak by nie doszła).
 */
export function DeleteDeviceDialog({
	device,
	open,
	onOpenChange,
}: {
	device: DeviceAgent;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const remove = useDeleteDeviceMutation();
	const label = deviceLabel(device);
	const reachable = isDeviceReachable(device);

	// Domyślnie odinstaluj, gdy online — „usuń urządzenie" zwykle znaczy „pozbądź
	// się go z komputera", nie tylko „zapomnij wpis". Re-seed przy każdym otwarciu.
	const [uninstall, setUninstall] = useState(reachable);
	useEffect(() => {
		if (open) setUninstall(reachable);
	}, [open, reachable]);

	function handleDelete() {
		remove.mutate(
			{ deviceUuid: device.deviceUuid, uninstall: reachable && uninstall },
			{
				onSuccess: () => {
					toast.success(
						reachable && uninstall
							? "Urządzenie usunięte — zlecono deinstalację agenta"
							: "Urządzenie zostało usunięte",
					);
					onOpenChange(false);
				},
				onError: () => toast.error("Nie udało się usunąć urządzenia"),
			},
		);
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>Usunąć urządzenie?</DialogTitle>
					<DialogDescription>
						„{label}" zostanie trwale usunięte z książki adresowej wraz z historią sesji zdalnych do
						niego. Tej operacji nie można cofnąć.
					</DialogDescription>
				</DialogHeader>
				<DialogBody className="space-y-3">
					{reachable ? (
						<label
							className={cn(
								"flex cursor-pointer items-start gap-2.5 rounded-md border p-3 text-sm",
								uninstall ? "border-destructive/50 bg-destructive/5" : "border-border",
							)}
						>
							<input
								type="checkbox"
								className="mt-0.5 h-4 w-4 shrink-0 accent-destructive"
								checked={uninstall}
								onChange={(e) => setUninstall(e.target.checked)}
								disabled={remove.isPending}
							/>
							<span>
								<span className="font-medium text-foreground">Odinstaluj agenta z komputera</span>
								<span className="mt-0.5 block text-xs text-muted-foreground">
									Agent zatrzyma usługę i usunie się z hosta. Bez tego zostaje zainstalowany i przy
									restarcie zgłosi się ponownie jako oczekujący.
								</span>
							</span>
						</label>
					) : (
						<p className="text-sm text-muted-foreground">
							Urządzenie jest offline — usunę tylko wpis. Agent (jeśli nadal zainstalowany) zgłosi
							się ponownie po powrocie online; deinstalację trzeba będzie wykonać lokalnie.
						</p>
					)}
				</DialogBody>
				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)} disabled={remove.isPending}>
						Anuluj
					</Button>
					<Button variant="destructive" onClick={handleDelete} disabled={remove.isPending}>
						{remove.isPending ? (
							<Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
						) : (
							<Trash2 className="mr-1.5 h-4 w-4" aria-hidden />
						)}
						{reachable && uninstall ? "Usuń i odinstaluj" : "Usuń urządzenie"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
